import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import StudentManagementService from '@core/services/student.portal.service';
import Modal from '@shared/components/Modal';
import { PortalButton } from '@shared/components/button/PortalButton';
import { CircleSpin } from 'assets/icons/CircleSpin';
import { AiOutlineDelete } from 'react-icons/ai';

type Props = {
  student: { id: number; username: string; fullname?: string; is_active: boolean | null };
  onDeleted: () => void | Promise<void>;
  onRejected: () => void | Promise<void>;
  disabled?: boolean;
  onBusyChange?: (busy: boolean) => void;
};

export default function DeleteStudentButton({ student, onDeleted, onRejected, disabled, onBusyChange }: Props) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);

  const close = () => {
    if (!pending.current) setShow(false);
  };

  const remove = async () => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    onBusyChange?.(true);
    setError('');
    try {
      const response = await StudentManagementService.deleteStudent(student.id);
      if (response?.status === 1) {
        setShow(false);
        toast.success('Student deleted. History has been retained.');
        await onDeleted();
      } else {
        const message = response?.message || 'Unable to delete student. Check your connection and permissions, then try again.';
        setError(message);
        toast.error(message);
        if (response?.code === 'StudentNotInactive' || response?.code === 'NotFound') {
          setShow(false);
          await onRejected();
        }
      }
    } catch {
      setError('Unable to complete the request. Refresh the student list before retrying.');
    } finally {
      pending.current = false;
      setBusy(false);
      onBusyChange?.(false);
    }
  };

  if (student.is_active !== false) return null;
  return <>
    <button type="button" disabled={disabled || busy}
      className="inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded border border-D92D20 bg-white px-2 text-sm font-medium leading-none text-D92D20 transition-colors hover:bg-FFEDE9 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-D92D20 disabled:cursor-not-allowed disabled:opacity-50"
      onClick={() => { setError(''); setShow(true); }}>
      <AiOutlineDelete size={16} aria-hidden="true" />
      Delete
    </button>
    <Modal show={show} title="Delete student?" onClose={close} className="max-w-[480px] font-Roboto">
      <p className="mb-4">Delete {student.fullname || student.username} ({student.username})? The account will be hidden and lose access. Enrollments, assignments and reading history will be retained, and this username cannot be reused.</p>
      {error && <p role="alert" className="text-redCustom-3b mb-4">{error}</p>}
      <div className="flex gap-4">
        <PortalButton disabled={busy} className="!bg-white border border-Maximum_Blue" onClick={close}>Cancel</PortalButton>
        <PortalButton disabled={busy} className="!bg-D92D20 !text-white" onClick={remove}>
          {busy && <CircleSpin />}{busy ? 'Deleting...' : 'Confirm delete'}
        </PortalButton>
      </div>
    </Modal>
  </>;
}
